import React, { useCallback, useState } from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import exportLargeTokens from '@amzn/meridian-tokens/base/icon/export-large';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { displayTime, getBusiness, extractRoleNameFromKey } from '../../../utils';
import { chainWalk } from '../../../../../utils/helpers';
import { BUSINESS_VIEW_NAME, ALL_SPEEDS, DEFAULT_SPEED, DEPARTMENTS } from '../../../../../utils/constants';
import { getIdleHC } from './ShiftHeadcount';
import { getSpeedFromRoles } from '../../../../../utils/roleSpeedUtils';

const createBusinessSpeedFields = (businessPrefix, businessLabel, speedsToShow) => {
  const fields = [{ key: `${businessPrefix}Business`, label: businessLabel, isChildRow: false, hasChildRows: true }];

  speedsToShow.forEach((speed) => {
    const speedKey = `${businessPrefix}_speed_${speed}`;
    fields.push({
      key: speedKey,
      label: `${speed}`,
      isChildRow: true,
      hasChildRows: true,
      parentKey: `${businessPrefix}Business`
    });

    DEPARTMENTS.forEach((dept) => {
      fields.push({
        key: `${speedKey}_${dept}`,
        label: dept.charAt(0).toUpperCase() + dept.slice(1),
        isChildRow: true,
        hasChildRows: false,
        parentKey: speedKey
      });
    });
  });

  return fields;
};

/**
 * Creates field configuration for a business with expandable department breakdown
 * @param {string} businessKey - The key for the business (e.g., 'coreAssigned', 'groceryAssigned', 'totalNeeded')
 * @param {string} businessLabel - The display label for the business (e.g., 'SSD Needed', 'Grocery Needed')
 * @returns {Array} Array of field objects with parent business and child department fields
 */
const createBusinessDepartmentFields = (businessKey, businessLabel) => {
  const fields = [{ key: businessKey, label: businessLabel, isChildRow: false, hasChildRows: true }];

  DEPARTMENTS.forEach((dept) => {
    let departmentKey;
    if (businessKey === `${BUSINESS_VIEW_NAME.CORE}Assigned`) {
      departmentKey = `${BUSINESS_VIEW_NAME.CORE}${dept.charAt(0).toUpperCase() + dept.slice(1)}`;
    } else if (businessKey === `${BUSINESS_VIEW_NAME.GROCERY}Assigned`) {
      departmentKey = `${BUSINESS_VIEW_NAME.GROCERY}${dept.charAt(0).toUpperCase() + dept.slice(1)}`;
    } else {
      departmentKey = `total${dept.charAt(0).toUpperCase() + dept.slice(1)}`;
    }

    fields.push({
      key: departmentKey,
      label: dept.charAt(0).toUpperCase() + dept.slice(1),
      isChildRow: true,
      hasChildRows: false,
      parentKey: businessKey
    });
  });

  return fields;
};

const getDisplayFields = (isGroceryEnabled, speedsToShow, isRushIndirectRulesEnabled) => {
  const baseFields = [
    { key: 'displayTime', label: '', isChildRow: false, hasChildRows: false },
    { key: 'totalAvailable', label: 'Total Available', isChildRow: false, hasChildRows: false }
  ];

  const speedFields = [];

  if (isRushIndirectRulesEnabled) {
    // When rushIndirect is enabled, show SSD Needed per 3 speeds with department breakdown
    speedFields.push(...createBusinessSpeedFields(BUSINESS_VIEW_NAME.CORE, 'SSD Needed', speedsToShow));
    // If grocery is enabled, also show Grocery Needed per 3 speeds with department breakdown
    if (isGroceryEnabled) {
      speedFields.push(...createBusinessSpeedFields(BUSINESS_VIEW_NAME.GROCERY, 'Grocery Needed', speedsToShow));
    }
  } else {
    // When rushIndirect is not enabled, show departments for each business
    if (isGroceryEnabled) {
      // Dynamically generate SSD Needed and Grocery Needed with department breakdowns
      speedFields.push(...createBusinessDepartmentFields(`${BUSINESS_VIEW_NAME.CORE}Assigned`, 'SSD Needed'));
      speedFields.push(...createBusinessDepartmentFields(`${BUSINESS_VIEW_NAME.GROCERY}Assigned`, 'Grocery Needed'));
    } else {
      // Show Total Needed with department breakdown
      speedFields.push(...createBusinessDepartmentFields('totalNeeded', 'Total Needed'));
    }
  }

  const remainingFields = [
    { key: 'totalIdle', label: 'Idle', isChildRow: false, hasChildRows: false },
    { key: 'breakOrMove', label: 'Break/Move', isChildRow: false, hasChildRows: true },
    { key: 'toBreak', label: 'To Break', isChildRow: true, hasChildRows: false, parentKey: 'breakOrMove' },
    { key: 'idleToProcess', label: 'Idle to Process', isChildRow: true, hasChildRows: false, parentKey: 'breakOrMove' },
    { key: 'movePPToPP', label: 'Move PP to PP', isChildRow: true, hasChildRows: false, parentKey: 'breakOrMove' },
    { key: 'onMoveOrBreak', label: 'On Move/Break', isChildRow: true, hasChildRows: false, parentKey: 'breakOrMove' }
  ];

  return [...baseFields, ...speedFields, ...remainingFields];
};

const isDifferentHeadcount = (current, previous) => {
  return (
    Object.keys(current)
      .filter((k) => k !== 'startDatetimeLocal')
      .filter((k) => current[k] !== previous[k]).length > 0
  );
};

const findDepartmentHeadcount = (quarterPivot, departmentName, business, plan = null) => {
  return Object.keys(quarterPivot)
    .filter((k) => k.startsWith(departmentName))
    .filter((k) => getBusiness(k, plan) === business)
    .reduce((acc, k) => acc + (quarterPivot[k] || 0), 0);
};

/**
 * Finds headcount by filtering roles based on business, speed, and department.
 * @returns {number} Total headcount matching the filters
 */
const findHeadcountByFilters = (quarterPivot, business, speed, roles, plan = null, departmentName = null) => {
  return Object.keys(quarterPivot)
    .filter((roleKey) => {
      if (departmentName && !roleKey.startsWith(departmentName)) {
        return false;
      }

      const belongsToBusiness = getBusiness(roleKey, plan) === business;

      // Extract role name from the key
      const roleName = extractRoleNameFromKey(roleKey);
      const roleSpeed = getSpeedFromRoles(roles, roleName);

      return belongsToBusiness && roleSpeed === speed;
    })
    .reduce((acc, k) => acc + (quarterPivot[k] || 0), 0);
};

const findHeadcountByBusinessAndSpeed = (quarterPivot, business, speed, roles, plan = null) => {
  return findHeadcountByFilters(quarterPivot, business, speed, roles, plan);
};

const findDepartmentHeadcountBySpeed = (quarterPivot, departmentName, business, speed, roles, plan = null) => {
  return findHeadcountByFilters(quarterPivot, business, speed, roles, plan, departmentName);
};

const getProcessedPivotData = (
  shift,
  headcountAllocationPivot,
  business = BUSINESS_VIEW_NAME.CORE,
  headcountAllocation,
  isGroceryEnabled = false,
  speedsToShow,
  plan = null
) => {
  const processedPivotData = [];
  const roles = chainWalk(() => plan.input.ssdShiftPlanInput.roleDetailsList, []);
  headcountAllocationPivot
    .filter(({ start_datetime_local }) => start_datetime_local >= shift.start && start_datetime_local < shift.end)
    .forEach((pivot) => {
      const { start_datetime_local, available_headcount, to_break, on_move_or_break, idle_to_process, move_pp_to_pp } =
        pivot;

      const coreOutbound = findDepartmentHeadcount(pivot, 'outbound', BUSINESS_VIEW_NAME.CORE, plan);
      const coreSortation = findDepartmentHeadcount(pivot, 'sortation', BUSINESS_VIEW_NAME.CORE, plan);
      const coreInjection = findDepartmentHeadcount(pivot, 'injection', BUSINESS_VIEW_NAME.CORE, plan);
      const coreAssigned = coreOutbound + coreSortation + coreInjection;

      const groceryOutbound = findDepartmentHeadcount(pivot, 'outbound', BUSINESS_VIEW_NAME.GROCERY, plan);
      const grocerySortation = findDepartmentHeadcount(pivot, 'sortation', BUSINESS_VIEW_NAME.GROCERY, plan);
      const groceryInjection = findDepartmentHeadcount(pivot, 'injection', BUSINESS_VIEW_NAME.GROCERY, plan);
      const groceryAssigned = groceryOutbound + grocerySortation + groceryInjection;

      const timeSpecificShift = { start: start_datetime_local, end: start_datetime_local + 1 };
      const timeSpecificIdle = getIdleHC(timeSpecificShift, headcountAllocation);
      const totalIdle = timeSpecificIdle ? timeSpecificIdle.total_hc || 0 : 0;

      const breakMove = to_break + on_move_or_break + idle_to_process + move_pp_to_pp;

      const pivotData = {
        startDatetimeLocal: displayTime(start_datetime_local),
        totalAvailable: available_headcount,
        coreAssigned,
        coreOutbound,
        coreSortation,
        coreInjection,
        groceryAssigned,
        groceryOutbound,
        grocerySortation,
        groceryInjection,
        totalNeeded: coreAssigned + groceryAssigned,
        totalOutbound: coreOutbound + groceryOutbound,
        totalSortation: coreSortation + grocerySortation,
        totalInjection: coreInjection + groceryInjection,
        totalIdle,
        breakOrMove: breakMove,
        toBreak: to_break,
        idleToProcess: idle_to_process,
        movePPToPP: move_pp_to_pp,
        onMoveOrBreak: on_move_or_break
      };

      pivotData.coreBusiness = coreAssigned;
      if (isGroceryEnabled) {
        pivotData.groceryBusiness = groceryAssigned;
      }

      const businessConfigs = [
        { prefix: BUSINESS_VIEW_NAME.CORE, businessType: BUSINESS_VIEW_NAME.CORE, enabled: true },
        { prefix: BUSINESS_VIEW_NAME.GROCERY, businessType: BUSINESS_VIEW_NAME.GROCERY, enabled: isGroceryEnabled }
      ];

      businessConfigs.forEach(({ prefix, businessType, enabled }) => {
        if (!enabled) return;

        speedsToShow.forEach((speed) => {
          const speedKey = `${prefix}_speed_${speed}`;
          pivotData[speedKey] = findHeadcountByBusinessAndSpeed(pivot, businessType, speed, roles, plan);
          DEPARTMENTS.forEach((dept) => {
            pivotData[`${speedKey}_${dept}`] = findDepartmentHeadcountBySpeed(
              pivot,
              dept,
              businessType,
              speed,
              roles,
              plan
            );
          });
        });
      });

      if (
        processedPivotData.length === 0 ||
        isDifferentHeadcount(pivotData, processedPivotData[processedPivotData.length - 1])
      ) {
        processedPivotData.push(pivotData);
      }
    });
  processedPivotData.forEach((pivot, index) => {
    if (index < processedPivotData.length - 1) {
      pivot.displayTime = `${pivot.startDatetimeLocal} - ${processedPivotData[index + 1].startDatetimeLocal}`;
    } else {
      pivot.displayTime = `${pivot.startDatetimeLocal} - ${displayTime(shift.end)}`;
    }
  });
  return processedPivotData;
};

const ShiftHeadCountExpanded = ({
  shift,
  headcountAllocationPivot,
  business = BUSINESS_VIEW_NAME.CORE,
  headcountAllocation,
  isGroceryEnabled = false,
  isRushIndirectRulesEnabled = false,
  plan = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  let speedsToShow = isRushIndirectRulesEnabled ? ALL_SPEEDS : [DEFAULT_SPEED];

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const processedPivotData = getProcessedPivotData(
    shift,
    headcountAllocationPivot,
    business,
    headcountAllocation,
    isGroceryEnabled,
    speedsToShow,
    plan,
    isRushIndirectRulesEnabled
  );

  const displayFields = getDisplayFields(isGroceryEnabled, speedsToShow, isRushIndirectRulesEnabled);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <Column>
      <Row alignmentHorizontal="end" widths="fit">
        <div style={{ cursor: 'pointer' }} onClick={() => setIsOpen(true)}>
          <Icon tokens={exportLargeTokens}></Icon>
        </div>
      </Row>

      <Modal title="Shift Headcount" open={isOpen} onClose={onClose} closeLabel="Close">
        <Row widths="fill" spacingInset="400">
          <Table headerRows={1} spacing="small">
            {displayFields
              .filter(({ isChildRow, parentKey }) => {
                if (!isChildRow) return true;

                if (!expandedSections[parentKey]) return false;

                const parentField = displayFields.find((field) => field.key === parentKey);
                if (parentField && parentField.isChildRow && parentField.parentKey) {
                  return expandedSections[parentField.parentKey];
                }

                return true;
              })
              .map(({ key, isChildRow, label, hasChildRows }) => (
                <TableRow key={key} backgroundColor={isChildRow ? 'highlight' : 'principal'}>
                  <TableCell alignmentVertical="top">
                    {hasChildRows && (
                      <Button type="icon" size="small" onClick={() => toggleSection(key)}>
                        {expandedSections[key] ? (
                          <Icon tokens={chevronDownSmallTokens}>Collapse rows</Icon>
                        ) : (
                          <Icon tokens={chevronRightSmallTokens}>Expand rows</Icon>
                        )}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>{label}</TableCell>
                  {processedPivotData.map((pivot) => (
                    <TableCell key={`${pivot.displayTime}-${key}`}>{pivot[key]}</TableCell>
                  ))}
                </TableRow>
              ))}
          </Table>
        </Row>
        <ModalFooter>
          <Row alignmentHorizontal="end" widths="fit">
            <Button type="secondary" size="small" onClick={onClose}>
              Close
            </Button>
          </Row>
        </ModalFooter>
      </Modal>
    </Column>
  );
};
export default ShiftHeadCountExpanded;
